import {
  Box,
  BoxProps,
  Flex,
  HStack,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react'
import { ContentNode } from '@helo/cms'
import { DataTypeConfigUtils } from '@helo/cms'
import { pipe } from '@helo/utils'
import { If } from '@helo/utils'
import { useRef } from 'react'
import { Link as _Link } from 'react-router-dom'

import { InfoOutlineIcon } from '../../components/Icons/InfoOutlineIcon'
import { Skeleton } from '../../components/SkeletonLoader'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { createWidget } from '../widget-utils'
import { HeightSyncWrapper } from './height-synchronizer'

export type DetailWidgetProps = BoxProps & {
  contentNode: ContentNode
  value: unknown
  isLoading: boolean
  emphasize?: boolean
  labelRight?: string | ContentNode
  label?: string
  align?: 'left' | 'center' | 'right'
  /**
   * Optional Chakra `Text` props merged onto the label after defaults
   * (same idea as Bento `separatorStyle`).
   */
  labelStyle?: TextProps
}

// GraphQL types from supergraph that have both tagName and tagValue fields and end with 'Tag'
const TAG_TYPE_NAMES = [
  'CatalogItemPurchaseOrderTag',
  'CatalogItemTag',
  'PurchaseOrderCiPoTag',
  'ShipmentDocumentCiPoTag',
  'ShipmentDocumentFulfillmentItemTag',
  'ShipmentDocumentFulfillmentItemOrderTag',
  'ShipmentDocumentTag',
  'TransportUnitCiPoTag',
  'TransportUnitClusterTag',
  'TransportUnitClusterCiPoTag',
  'TransportUnitShipmentDocumentTag',
]

export const DetailWidget = ({
  value,
  contentNode,
  emphasize,
  label,
  labelRight,
  labelStyle,
  isLoading = false,
  align = 'left',
  ...props
}: DetailWidgetProps) => {
  const Link = contentNode?.state?.linkUrl ? _Link : Box
  const size = contentNode?.state?.size || 'medium'
  const ref = useRef<HTMLDivElement>(null)

  // For tag types, use tagName as the label if no explicit label is provided
  const isTagType =
    contentNode?.typeName && TAG_TYPE_NAMES.includes(contentNode.typeName)
  const tagName = isTagType ? (contentNode?.data as any)?.tagName : undefined
  const labelText = label ?? contentNode?.state?.label ?? tagName

  const gap = contentNode?.state?.size === 'large' ? '4px' : '8px'

  const alignItems = {
    left: 'stretch',
    center: 'center',
    right: 'flex-end',
  }[align]

  // Check if labelRight is a ContentNode or string
  const isLabelRightContentNode =
    labelRight && typeof labelRight === 'object' && 'kind' in labelRight

  // Merge {labelHidden: true} into options if dataType is 'tag'

  // TODO: investigate this.
  // const isTag = dataType === 'tag'
  // const finalOptions = isTag ? { ...options, hideLabel: true } : options

  const labelRightContentNode =
    labelRight &&
    pipe(
      labelRight as ContentNode,
      If.then(
        config => config.kind === 'FIELD',
        DataTypeConfigUtils.getOfType(),
      ),
      DataTypeConfigUtils.updateState({
        styles: {
          fontSize: '12px',
          fontWeight: '700',
          color: 'presets.neutral.80',
        },
      }),
    )

  return (
    <VStack
      as={Link}
      gap={gap}
      alignItems={alignItems}
      color="presets.turquoise.100" // to override Link's color styling. Not ideal.
      to={contentNode?.state?.linkUrl}
      ref={ref}
      {...props}
    >
      <HeightSyncWrapper group="detail-widget-label" rootRef={ref}>
        <HStack
          gap="6px"
          alignItems="start"
          justifyContent={contentNode?.state?.contentAlignment || 'start'}
        >
          <Skeleton isLoading={isLoading} h="12px" my="2px" flex="1">
            <Text
              fontSize="12px"
              fontWeight={!emphasize ? '500' : '600'}
              color="presets.neutral.80"
              display="-webkit-box"
              overflow="hidden"
              css={{
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
              {...labelStyle}
            >
              {contentNode?.state?.tooltip && (
                <Tooltip label={contentNode?.state?.tooltip}>
                  <InfoOutlineIcon
                    float="right"
                    boxSize="16px"
                    marginLeft="4px"
                    color={
                      (labelStyle?.color as string | undefined) ??
                      'presets.neutral.80'
                    }
                  />
                </Tooltip>
              )}
              {labelText}
              {labelRight && !isLoading && ' | '}
            </Text>
          </Skeleton>
          {labelRight && !isLoading && (
            <>
              {isLabelRightContentNode ? (
                <TypeRenderer
                  contentNode={labelRightContentNode as ContentNode}
                  isLoading={isLoading}
                />
              ) : (
                <Text
                  fontSize="12px"
                  color="presets.neutral.80"
                  fontWeight="700"
                >{`${labelRight}`}</Text>
              )}
            </>
          )}
        </HStack>
      </HeightSyncWrapper>
      <HeightSyncWrapper group="detail-widget-value" rootRef={ref}>
        <Flex justifyContent={contentNode?.state?.contentAlignment || 'start'}>
          <TypeRenderer contentNode={contentNode} isLoading={isLoading} />
        </Flex>
      </HeightSyncWrapper>
    </VStack>
  )
}

export const DetailWidgetDefinition = createWidget(DetailWidget, {
  type: 'Detail',
  slots: [],
  isDataDriven: true,
})
